import React from 'react';

import { ApolloError } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router';

import { appRoutes } from '@clearverify/constants';

import { useCognitoGetSsoUrlLazyQuery } from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { SSO_STATE_STORAGE_KEY } from 'src/constants/auth';
import { useCognito, useNotification } from 'src/hooks/app';
import { useAppAuth } from 'src/hooks/auth';
import { UserNotProvisionedError } from 'src/providers/AuthProvider';

export const SsoCallback: React.FC = () => {
  const { setAuthState } = useAppAuth();
  const { signInWithSso } = useCognito();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { spawnNotification, createApolloContext } = useNotification();
  const [cognitoGetSsoUrl] = useCognitoGetSsoUrlLazyQuery({
    context: createApolloContext({ errorMessage: 'SSO sign-in failed' }),
  });
  // Authorization code is single-use; guards against StrictMode double-running the effect
  const isExchangeStarted = React.useRef(false);

  React.useEffect(() => {
    if (isExchangeStarted.current) return;
    isExchangeStarted.current = true;

    const exchangeCode = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const expectedState = sessionStorage.getItem(SSO_STATE_STORAGE_KEY);
      sessionStorage.removeItem(SSO_STATE_STORAGE_KEY);
      // `state` must match the one stored by SsoLogin, else a foreign code could log the user in
      // (login-CSRF).
      if (!code || !state || state !== expectedState) {
        throw new Error('Invalid authorization response');
      }

      const { data, error } = await cognitoGetSsoUrl();
      if (error) throw error;
      if (!data?.cognitoGetSsoUrl) {
        throw new Error('SSO is not configured');
      }

      const { idToken, accessToken } = await signInWithSso({
        code,
        ssoHost: new URL(data.cognitoGetSsoUrl).origin,
        redirectUrl: new URL(appRoutes.auth.ssoCallback, window.location.origin).href,
      });
      if (!idToken) {
        throw new Error('Unauthorized');
      }

      await setAuthState({ bearerToken: idToken, accessToken });
    };

    exchangeCode().catch((error: unknown) => {
      // GraphQL errors are already toasted by the global handler in ApolloProvider.
      const isReported = error instanceof ApolloError && error.graphQLErrors.length > 0;
      if (!isReported) {
        spawnNotification(
          error instanceof UserNotProvisionedError
            ? error.message
            : 'Incorrect login information or this user does not have access to this product',
          'error',
        );
      }
      // `replace` drops the single-use ?code= so back/reload does not retry the exchange.
      navigate(appRoutes.auth.login, { replace: true });
    });
  }, [searchParams, cognitoGetSsoUrl, signInWithSso, setAuthState, spawnNotification, navigate]);

  return <FullscreenLoader sx={{ height: 400 }} />;
};
